import { useState } from 'react';
import { Bloque, Pagina, Tabla } from '../componentes/Estructura';
import { Cargando, ConError, Vacio } from '../componentes/Estados';
import { DialogoMotivo } from '../componentes/DialogoMotivo';
import { laFechaHora } from '../componentes/formato';
import { useCargaReal } from '../dominio/carga';
import { ErrorApi } from '../dominio/clienteApi';
import { obtenerPoliticaPrecio, publicarPoliticaPrecio } from '../dominio/politicaPrecioApi';
import type { ModeloDePrecio } from '../dominio/politicaPrecioApi';
import {
  CAMPOS,
  armarPublicacion,
  borradorDe,
  calcularEstadoPublicacionPrecio,
} from './precio.logica';
import type { BorradorPrecio } from './precio.logica';

/**
 * Política de precio — la pantalla que decide **si la plataforma se compromete o no con el precio
 * de un viaje**. Es la escritura de mayor alcance de la consola: bajo `RANGO_CON_TECHO` o
 * `FIJO_VINCULANTE`, publicar crea una obligación frente a conductores y pasajeros.
 *
 * Tres decisiones de esta pantalla, con su motivo:
 *
 * 1. **La lista de modelos y su estado la trae el BACKEND** (`catalogo`), no está escrita acá. Ver
 *    la cabecera de `dominio/politicaPrecioApi.ts`: dos listas envejecen por separado y las dos
 *    direcciones de divergencia hacen daño.
 * 2. **La ADVERTENCIA se muestra ANTES de confirmar, no después.** Cinco de los ocho modelos
 *    comprometen algo que la plataforma sólo puede verificar por RECLAMO (R-05: no registra el
 *    monto realmente pagado). Eso NO bloquea la publicación — se publica sabiéndolo. Lo que el
 *    sistema tiene que garantizar es que quien firma la versión lo lea antes de apretar, no que
 *    lo descubra cuando llegue el primer reclamo de sobrecobro.
 * 3. **El formulario pide los parámetros que el modelo USA, y sólo esos.** No es cosmética: el
 *    backend rechaza con 400 tanto el que falta como el que sobra, y un formulario que pidiera
 *    los tres siempre haría que el operador escribiera números que la política va a descartar.
 *
 * La lógica de habilitación y el armado del cuerpo viven en `precio.logica.ts` — sin React, para
 * que se puedan ejercitar solos. Ver la cabecera de ese archivo.
 */

/** El mensaje que ve el operador cuando la publicación falla. `ErrorApi` ya trae el motivo del backend. */
const mensajeDeFalla = (error: unknown): string =>
  error instanceof ErrorApi ? error.message : 'Ocurrió un error inesperado. Probá de nuevo.';

/** Cómo se muestra un parámetro en la tabla del historial. `null` = el modelo no lo usaba. */
const celda = (valor: number | null, sufijo: string): string =>
  valor === null ? '—' : `${valor}${sufijo}`;

export function Precio() {
  const [carga, recargar] = useCargaReal(() => obtenerPoliticaPrecio(), []);

  const [borrador, setBorrador] = useState<BorradorPrecio | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [publicando, setPublicando] = useState(false);
  const [errorAccion, setErrorAccion] = useState<string | null>(null);

  if (carga.estado === 'cargando') {
    return (
      <Pagina rotulo="Solo administrador" titulo="Política de precio">
        <div className="bloque">
          <Cargando filas={6} etiqueta="Cargando la política de precio" />
        </div>
      </Pagina>
    );
  }

  if (carga.estado === 'error') {
    return (
      <Pagina rotulo="Solo administrador" titulo="Política de precio">
        <div className="bloque">
          <ConError detalle={carga.mensaje} alReintentar={recargar} />
        </div>
      </Pagina>
    );
  }

  const { efectivos, historial, catalogo } = carga.datos;

  /**
   * La versión que está VIGENTE en la tabla, o `undefined` si no hay ninguna.
   *
   * 🔴 **No es `historial[0]`.** Con el fallback activo el historial puede estar vacío, o traer
   * sólo versiones cerradas — y en los dos casos `historial[0]` mentiría diciendo que hay una
   * vigente. `vigenteHasta === null` es el mismo criterio que usa el backend para cerrarla.
   */
  const vigente = historial.find((version) => version.vigenteHasta === null);

  /**
   * Espeja `(anterior?.version ?? 0) + 1` del backend. Sin fila vigente la próxima es la 1 — y no
   * `efectivos.version + 1`, que ni siquiera es un número cuando el origen es FALLBACK
   * (`efectivos.version` es `null` justamente para no inventar una versión que no existe).
   */
  const proximaVersion = vigente ? vigente.version + 1 : 1;

  const valores = borrador ?? borradorDe(efectivos);

  const estado = calcularEstadoPublicacionPrecio({
    borrador: valores,
    efectivos,
    catalogo,
    proximaVersion,
    publicando,
  });
  const { ficha, hayCambios, puedePublicar, textoBoton, ayuda, advertencia } = estado;

  async function publicar(motivo: string): Promise<void> {
    if (!ficha) return;
    setPublicando(true);
    setErrorAccion(null);
    try {
      await publicarPoliticaPrecio(armarPublicacion(valores, ficha, motivo));
      setConfirmando(false);
      setBorrador(null);
      recargar();
    } catch (error) {
      setConfirmando(false);
      setErrorAccion(mensajeDeFalla(error));
      // 🔴 El borrador NO se descarta cuando falla. Si la publicación se rechazó (un 409 porque
      // otro operador publicó en simultáneo, un 400 del gate), tirar lo que el operador acababa
      // de elegir lo obliga a rehacerlo. Se recarga el estado para que compare contra lo que
      // quedó vigente de verdad, y el borrador sigue en pantalla.
      recargar();
    } finally {
      setPublicando(false);
    }
  }

  const fichaVigente = catalogo.find((f) => f.modelo === efectivos.modelo);

  return (
    <Pagina
      rotulo="Solo administrador"
      titulo="Política de precio"
      ayuda="Qué dice la plataforma sobre el precio de un viaje, y hasta dónde se compromete. Cada cambio crea una versión nueva con tu nombre y el motivo, así después se puede correlacionar un cambio de política con los reclamos que vinieron."
    >
      {errorAccion ? (
        <div className="bloque" style={{ marginBottom: 0 }}>
          <ConError titulo="No se pudo publicar la versión" detalle={errorAccion} />
        </div>
      ) : null}

      {/*
        🔴 `origen === 'FALLBACK'` significa que NINGUNA VERSIÓN ESTÁ GOBERNANDO EL PRECIO: no hay
        política publicada, o la lectura falló. Los flujos siguen funcionando con el modelo por
        defecto —que es el que no compromete nada, a propósito— pero los viajes de ese rato quedan
        SIN `politicaPrecioId`, o sea no atribuibles a ninguna versión. Sin este aviso, la pantalla
        mostraría el mismo modelo que en el caso sano.
      */}
      {efectivos.origen === 'FALLBACK' ? (
        <div className="bloque" style={{ marginBottom: 0 }}>
          <ConError
            titulo="El precio no lo está gobernando ninguna versión"
            detalle={`No hay ninguna política publicada (o no se pudo leer), así que el sistema está aplicando el modelo por defecto: ${fichaVigente?.titulo ?? efectivos.modelo}. Es el que no le promete nada a nadie, pero los viajes de este rato no quedan atribuidos a ninguna versión. Publicá una versión para corregirlo.`}
            alReintentar={recargar}
          />
        </div>
      ) : null}

      <Bloque
        titulo={
          vigente
            ? `Versión ${vigente.version}, vigente`
            : 'Sin versión vigente — modelo por defecto'
        }
        nota={
          vigente
            ? `Desde ${laFechaHora(vigente.vigenteDesde)} · la puso ${vigente.editadoPor ?? 'sin registrar'}`
            : 'Esto es el modelo declarado por defecto, no una versión guardada.'
        }
      >
        <label className="campo">
          <span className="campo__etiqueta rotulo">Modelo de precio</span>
          <select
            className="selector"
            value={valores.modelo}
            onChange={(evento) =>
              setBorrador({ ...valores, modelo: evento.target.value as ModeloDePrecio })
            }
          >
            {/*
              El catálogo llega en orden de MENOS a MÁS compromiso de la plataforma, y se muestra
              tal cual: el orden es lo que cuenta la historia. Los bloqueados se listan igual, con
              su motivo — esconderlos haría que el operador no supiera que existen ni por qué no
              están, y el pedido volvería como "¿se puede hacer X?" sin respuesta a mano.
            */}
            {catalogo.map((f) => (
              <option key={f.modelo} value={f.modelo}>
                {f.titulo}
                {f.publicable ? '' : ' — todavía no disponible'}
              </option>
            ))}
          </select>
          <span className="campo__ayuda">{ficha?.descripcion}</span>
        </label>

        {/*
          🔴 EL GATE, dicho en la pantalla y no sólo en el 400. Un operador que elige un modelo
          bloqueado tiene que saber QUÉ FALTA antes de escribir un motivo y apretar publicar.
        */}
        {estado.modeloBloqueado && ficha ? (
          <div className="estado" role="status" style={{ marginTop: 12 }}>
            <p className="estado__titulo">Este modelo todavía no se puede publicar</p>
            <p className="estado__detalle">
              Falta {ficha.falta}. Publicarlo ahora dejaría al sistema prometiendo algo que ningún
              flujo hace cumplir.
            </p>
          </div>
        ) : null}

        {/*
          🔴 LA ADVERTENCIA DE R-05, y es DISTINTA del gate: no bloquea nada. Se muestra acá, antes
          de apretar el botón, y se repite dentro del diálogo de confirmación. Que apareciera
          después de publicar no serviría de nada — la decisión ya se tomó.
        */}
        {advertencia ? (
          <div className="estado" style={{ marginTop: 12, borderColor: 'var(--ambar)' }}>
            <p className="estado__titulo">La plataforma no puede verificar esto por su cuenta</p>
            <p className="estado__detalle">{advertencia}</p>
          </div>
        ) : null}

        {/*
          Sólo los parámetros que ESTE modelo usa. Pedir los tres siempre haría que el operador
          escribiera números que la publicación va a descartar — y que el backend rechace con 400
          si igual viajaran.
        */}
        {ficha && ficha.parametros.length > 0 ? (
          <div
            className="rejilla"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', marginTop: 12 }}
          >
            {ficha.parametros.map((p) => (
              <label className="campo" key={p}>
                <span className="campo__etiqueta rotulo">
                  {CAMPOS[p].etiqueta} <span className="apagado">· {CAMPOS[p].unidad}</span>
                </span>
                <input
                  className="entrada"
                  type="number"
                  min={p === 'toleranciaTechoPct' ? 0 : 1}
                  step={CAMPOS[p].paso}
                  value={valores[p]}
                  onChange={(evento) => setBorrador({ ...valores, [p]: evento.target.value })}
                />
                <span className="campo__ayuda">{CAMPOS[p].ayuda}</span>
                {efectivos[p] !== null ? (
                  <span className="campo__ayuda" style={{ color: 'var(--ambar)' }}>
                    Ahora está en {efectivos[p]}.
                  </span>
                ) : null}
              </label>
            ))}
          </div>
        ) : null}

        <div className="fila" style={{ marginTop: 6 }}>
          <button
            type="button"
            className="boton boton--principal"
            disabled={!puedePublicar}
            onClick={() => setConfirmando(true)}
          >
            {textoBoton}
          </button>
          <button
            type="button"
            className="boton"
            disabled={!hayCambios || publicando}
            onClick={() => setBorrador(null)}
          >
            Descartar cambios
          </button>
          {ayuda ? <span className="apagado">{ayuda}</span> : null}
        </div>
      </Bloque>

      <Bloque
        titulo="Los ocho modelos"
        nota="De menos a más compromiso de la plataforma. Los que todavía no se pueden publicar dicen qué falta construir — no es una decisión de producto, es maquinaria que no existe."
        sinRelleno
      >
        <Tabla cabeceras={['Modelo', 'Qué hace', 'Se puede publicar', 'Qué falta']}>
          {catalogo.map((f) => (
            <tr key={f.modelo} data-estado={f.publicable ? 'bueno' : undefined}>
              <td>
                {f.titulo}
                {f.advertencia ? (
                  <div className="apagado" title={f.advertencia}>
                    compromete algo no verificable
                  </div>
                ) : null}
              </td>
              <td style={{ fontFamily: 'var(--sans)', maxWidth: 320 }}>{f.descripcion}</td>
              <td>{f.publicable ? 'Sí' : 'Todavía no'}</td>
              <td style={{ fontFamily: 'var(--sans)', maxWidth: 380 }}>
                {f.falta ?? <span className="apagado">—</span>}
              </td>
            </tr>
          ))}
        </Tabla>
      </Bloque>

      <Bloque
        titulo="Historial"
        nota="Qué modelo rigió, desde cuándo, quién lo puso y por qué. Es lo que permite mirar un cambio de política contra los reclamos que vinieron después."
        sinRelleno
      >
        {historial.length === 0 ? (
          <div className="bloque__cuerpo">
            <Vacio
              titulo="Todavía no hay ninguna versión guardada"
              detalle="La primera que publiques va a aparecer acá, con tu nombre y el motivo."
            />
          </div>
        ) : (
          <Tabla
            cabeceras={[
              'Versión',
              'Desde',
              'Modelo',
              { texto: 'Ventana', num: true },
              { texto: 'Ofertas', num: true },
              { texto: 'Margen', num: true },
              'Quién',
              'Por qué',
            ]}
          >
            {historial.map((version) => (
              <tr
                key={version.id}
                data-estado={version.vigenteHasta === null ? 'bueno' : undefined}
              >
                <td>
                  {version.version}
                  {version.vigenteHasta === null ? <div className="apagado">vigente</div> : null}
                </td>
                <td className="tenue">{laFechaHora(version.vigenteDesde)}</td>
                <td>
                  {catalogo.find((f) => f.modelo === version.modelo)?.titulo ?? version.modelo}
                </td>
                {/* `—` = el modelo de esa versión no usaba ese parámetro. */}
                <td className="tabla__num">{celda(version.ventanaPrecioSeg, ' s')}</td>
                <td className="tabla__num">{celda(version.maxOfertasPrecio, '')}</td>
                <td className="tabla__num">{celda(version.toleranciaTechoPct, ' %')}</td>
                {/*
                  `editadoPor`/`motivo` pueden ser `null` (columnas nullables). NO se rellenan con
                  un centinela: la columna no distingue "nació con el sistema" de "alguien la
                  escribió por fuera y no quedó registrado", y ponerlo inventaría una atribución
                  sobre una versión que decide si la plataforma se compromete con el precio.
                */}
                <td>{version.editadoPor ?? <span className="apagado">sin registrar</span>}</td>
                <td style={{ fontFamily: 'var(--sans)', maxWidth: 340 }}>
                  {version.motivo ?? <span className="apagado">sin motivo registrado</span>}
                </td>
              </tr>
            ))}
          </Tabla>
        )}
      </Bloque>

      {confirmando && ficha ? (
        <DialogoMotivo
          titulo={`Publicar la versión ${proximaVersion} — ${ficha.titulo}`}
          descripcion={
            // La advertencia se REPITE acá a propósito. El operador puede haber elegido el modelo
            // hace minutos y estar leyendo sólo este diálogo; que la obligación no verificable
            // aparezca únicamente arriba dejaría el consentimiento apoyado en que se acuerde.
            (advertencia ? `${advertencia} ` : '') +
            'Esto cambia qué dice la plataforma sobre el precio desde el momento en que se guarda. ' +
            'Escribí qué esperás que pase: es lo que se va a leer al revisar los reclamos de la semana. ' +
            'Mínimo 10 caracteres — lo exige el backend.'
          }
          etiquetaConfirmar="Publicar versión"
          peligroso={advertencia !== null}
          alConfirmar={(motivo) => void publicar(motivo)}
          alCancelar={() => setConfirmando(false)}
        />
      ) : null}
    </Pagina>
  );
}
